import { useEffect, useRef } from 'react'

export function Wordmark({ compact = false }) {
  return <img className={`wordmark ${compact ? 'wordmark--compact' : ''}`} src="/brand/wordmark-on-dark.svg" alt="Untitled Academy" />
}

export function ArrowIcon({ back = false }) {
  return <span aria-hidden="true" className={back ? 'arrow arrow--back' : 'arrow'}>→</span>
}

export function Shell({ children, language, onLanguage, labels, homeUrl, footerWhatsApp, showLanguage = true }) {
  return (
    <div className="app-shell">
      <a className="skip-link" href="#main">{labels.skip}</a>
      <header className="topbar">
        <a className="logo-link" href={homeUrl} aria-label={labels.homeLabel}><Wordmark compact /></a>
        {showLanguage && (
          <button className="language-switch" onClick={onLanguage} aria-label={labels.changeLanguage}>
            <span className={language === 'en' ? 'active' : ''}>EN</span><span aria-hidden="true">/</span><span className={language === 'ml' ? 'active' : ''}>മ</span>
          </button>
        )}
      </header>
      <main id="main" className="main">{children}</main>
      <footer className="footnote">
        {footerWhatsApp ? <a href={`https://wa.me/${footerWhatsApp}`} target="_blank" rel="noreferrer">{labels.footerNote}</a> : <span>{labels.footerNote}</span>}
        <span>{labels.locations}</span>
      </footer>
    </div>
  )
}

export function Progress({ current, total = 16, label }) {
  const percentage = Math.max(0, Math.min(100, (current / total) * 100))
  return (
    <div className="progress-wrap" aria-label={label}>
      <div className="progress-meta"><span>{label}</span><span className="progress-spark" aria-hidden="true">✦</span></div>
      <div className="progress-track"><span style={{ width: `${percentage}%` }} /></div>
    </div>
  )
}

export function BackButton({ children, onClick }) {
  return <button className="back-button" onClick={onClick}><ArrowIcon back />{children}</button>
}

function MiniApp({ tidy = false }) {
  return (
    <div className={`mini-app ${tidy ? 'mini-app--tidy' : ''}`} aria-hidden="true">
      <div className="mini-status"><i /><i /><i /></div>
      <div className="mini-title" />
      <div className="mini-copy" />
      <div className="mini-grid"><i /><i /><i /></div>
      <div className="mini-action" />
    </div>
  )
}

function ProjectVisual({ option }) {
  if (option === 'app') return <div className="visual visual-phone"><div className="phone"><span>melo</span><i className="orb"/><i/><i/><b>Start focus</b></div><em>Useful, every day.</em></div>
  if (option === 'liveWebsite') return <div className="visual visual-web"><div className="browser"><span/><span/><span/><strong>Northbound</strong><h4>Make room for wonder.</h4><b>Explore</b></div><em>Designed. Built. Live.</em></div>
  if (option === 'brand') return <div className="visual visual-brand"><div className="poster">OLO<br/><small>slow-made soda</small></div><div className="post">new<br/>rituals</div><em>A world people recognise.</em></div>
  return <div className="visual visual-ad"><div className="ad-shape">48H</div><strong>MAKE<br/>IT MOVE.</strong><span>city studio week</span></div>
}

function ServiceVisual() {
  return <div className="service-site" aria-hidden="true"><div className="service-head"><b>CivicLink</b><span>Citizen portal</span></div><div className="service-nav">HOME &nbsp; SERVICES &nbsp; FORMS &nbsp; NOTICES &nbsp; HELP</div><div className="service-body"><aside>Quick links<br/>Applications<br/>Payments<br/>Certificates</aside><div><strong>Welcome to online services</strong><p>Find important forms, notices, and public information.</p><span className="fake-btn">Click here</span><span className="fake-btn">New update</span></div></div></div>
}

function PortfolioVisual({ option }) {
  const labels = { posts: 'NOVA / social', website: 'Lumen / web', screens: 'Move / app', notYet: 'Your first canvas' }
  return <div className={`portfolio-preview portfolio-preview--${option}`} aria-hidden="true"><span>{labels[option]}</span><i/><b>{option === 'notYet' ? '+' : '01'}</b></div>
}

function FutureVisual({ option }) {
  const copy = { apps: ['Product day', 'A useful flow takes shape'], websites: ['Launch day', 'Your site is live'], brands: ['Studio wall', 'A new identity comes alive'], campaigns: ['Campaign room', 'Ideas turn into results'] }
  return <div className={`future-scene future-scene--${option}`} aria-hidden="true"><div className="future-window"><i/><i/><i/></div><span>{copy[option][0]}</span><strong>{copy[option][1]}</strong></div>
}

function ComputerVisual({ option }) {
  const bars = { confident: 4, basic: 3, guidance: 2, beginning: 1 }[option]
  return <div className="computer-scene" aria-hidden="true"><div className="laptop"><div><span className="cursor-dot"/><b>hello, new skill</b></div></div><div className="confidence-bars">{[1,2,3,4].map(n=><i className={n<=bars?'on':''} key={n}/>)}</div></div>
}

export function QuestionVisual({ question, option, copy }) {
  if (question.visual === 'projects') return <ProjectVisual option={option} />
  if (question.visual === 'comparison' && (option === 'a' || option === 'b')) {
    return <img className="comparison-option-image" src={option === 'a' ? '/option-a.jpg' : '/option-b.jpg'} alt={copy[2] || ''} />
  }
  return null
}

export function OptionCard({ question, option, copy, selected, onSelect }) {
  const hasVisual = question.visual === 'projects' || (question.visual === 'comparison' && (option.id === 'a' || option.id === 'b'))
  return (
    <button type="button" className={`option-card ${selected ? 'is-selected' : ''} ${hasVisual ? 'has-visual' : ''} ${question.visual === 'comparison' ? 'comparison-option' : ''}`} {...(question.multi ? { 'aria-pressed': selected } : {})} onClick={onSelect}>
      <QuestionVisual question={question} option={option.id} copy={copy} />
      <span className="option-copy"><strong>{copy[0]}</strong>{copy[1] && <small>{copy[1]}</small>}</span>
      {question.multi && <span className="select-mark" aria-hidden="true">{selected ? '✓' : ''}</span>}
    </button>
  )
}

export function Dialog({ title, cancel, confirm, onCancel, onConfirm }) {
  const ref = useRef(null)
  useEffect(() => { ref.current?.focus() }, [])
  return <div className="dialog-backdrop" role="presentation" onMouseDown={(e)=>e.target===e.currentTarget&&onCancel()}><div className="dialog" role="alertdialog" aria-modal="true" aria-labelledby="dialog-title" tabIndex="-1" ref={ref}><span className="dialog-star">✦</span><h2 id="dialog-title">{title}</h2><div className="dialog-actions"><button className="button button--ghost" onClick={onCancel}>{cancel}</button><button className="button button--primary" onClick={onConfirm}>{confirm}</button></div></div></div>
}

export function DiscoveryLoading({ title, statements, active }) {
  return <section className="loading-screen" aria-live="polite" aria-busy="true"><div className="discovery-orbit" aria-hidden="true"><i/><i/><i/><b>✦</b></div><p className="eyebrow">Untitled Academy*</p><h1>{title}</h1><div className="loading-statements">{statements.map((item,index)=><span className={index===active?'active':index<active?'done':''} key={item}><i>{index<active?'✓':index+1}</i>{item}</span>)}</div></section>
}
